import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { Task } from '../../types/task';
import { useGetAllEmployeesQuery } from '../../features/apis/authApi';

interface EditTaskProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: Task) => void;
  task: Task;
  isLoading: boolean;
}

const EditTask: React.FC<EditTaskProps> = ({
  isOpen,
  onClose,
  onSubmit,
  task,
  isLoading,
}) => {
  console.log('task', task);
  const today = new Date().toISOString().split('T')[0];
  const [taskData, setTaskData] = useState<Task>({
    name: '',
    status: '',
    dependency: '',
    timeRequired: '',
    description: '',
    category: '',
    assignedDate: today,
    dueDate: today,
  });

  const { data } = useGetAllEmployeesQuery();

  useEffect(() => {
    if (task) {
      const toInputDate = (val: string | undefined) => {
        if (!val) return today;
        // already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        // handle DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
          const [day, month, year] = val.split('/');
          return `${year}-${month}-${day}`;
        }
        // handle "Apr 28, 2026" or "Apr 28 2026"
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0];
        }
        return today;
      };

      setTaskData({
        ...task,
        name: task.name || '',
        status: task.status || '',
        description: task.description || '',
        category: task.category || '',
        timeRequired: task.timeRequired || task.duration || '',
        assignedDate: toInputDate(task.assignedDate || task.allocatedDate),
        dueDate: toInputDate(task.dueDate),
        dependency: task.dependency || '',
      });
    }
  }, [task, today]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === 'dependency' && value === 'Other') {
      setTaskData((prev) => ({ ...prev, dependency: '' }));
    } else {
      setTaskData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    await onSubmit(taskData);
    onClose();
  };

  const isFormComplete = Object.values(taskData)
    .filter((value) => typeof value === 'string')
    .every((value) => value.trim() !== '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={!isLoading ? onClose : () => {}}
      size="2xl"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />{' '}
      <ModalContent borderRadius="20px" p={4}>
        <ModalHeader fontSize="28px" fontWeight="700">
          Edit Task
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <hr className="border-t-[0.25px] border-[#D4D4D4] mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm mb-1">Task</label>
              <input
                type="text"
                name="name"
                value={taskData.name}
                onChange={handleChange}
                placeholder="Please Specify"
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Status</label>
              <select
                name="status"
                value={taskData.status}
                onChange={handleChange}
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              >
                <option value="">Select Status</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Dependency</label>
              <select
                name="dependency"
                value={taskData.dependency || ''}
                onChange={handleChange}
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              >
                <option value="">Select an Employee</option>
                {data?.employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.name || employee.email}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Time Required (hrs)</label>
              <input
                type="number"
                name="timeRequired"
                value={taskData.timeRequired}
                onChange={handleChange}
                placeholder="e.g. 1.5"
                step="0.1"
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-sm focus:outline-none focus:border-[#D4D4D4]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Assigned Date</label>
              <input
                type="date"
                name="assignedDate"
                value={taskData.assignedDate}
                onChange={handleChange}
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={taskData.dueDate}
                onChange={handleChange}
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              />
            </div>

            <div className="mb-4 col-span-2">
              <label className="block text-sm mb-1">Description</label>
              <textarea
                name="description"
                value={taskData.description}
                onChange={handleChange}
                placeholder="Optional description"
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#D4D4D4]"
              />
            </div>

            <div className="mb-4 col-span-2">
              <label className="block text-sm mb-1">Category</label>
              <select
                name="category"
                value={taskData.category}
                onChange={handleChange}
                className="w-full border border-[#D4D4D4] rounded-lg px-3 py-3 h-12 text-gray-700 text-sm focus:outline-none focus:border-[#D4D4D4]"
              >
                <option value="">Select Category</option>
                <option value="Internal Tools">Internal Tools</option>
                <option value="Client Work">Client Work</option>
                <option value="Marketing / Sales">Marketing & Sales</option>
                <option value="People Operations">People Operations</option>
              </select>
            </div>
          </div>
        </ModalBody>

        <ModalFooter justifyContent="flex-start">
          <Button
            bg="#6E3EF5"
            color="white"
            borderRadius="10px"
            onClick={handleSubmit}
            _hover={{ bg: '#5930c7' }}
            isLoading={isLoading}
            loadingText="Updating..."
            isDisabled={!isFormComplete || isLoading}
            px={6}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditTask;
